import React, { useCallback, useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import * as gamesApi from '../../api/games';
import { formatKickoff } from '../../lib/formatDate';
import './gameDetails.css';

/**
 * The manager's say after a rollover (backlog item 49).
 *
 * A rollover puts everybody back in and wipes every pick. In a real game some people pay their
 * stake again and some do not, and new people may want in - which can double the prize. Whether
 * that is fair is the manager's call, so until the next round's pick deadline they can let
 * people in and take people out.
 *
 * Shown only while the server says the window is open (`rolloverWindowOpen`). The panel never
 * works the window out for itself: the server owns it, and a browser deciding from the game's
 * status is how the remove button used to be wrong.
 */
function RolloverPanel({ game, currentUser, onChanged }) {
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const gameId = game?.id;
  const open = Boolean(game?.rolloverWindowOpen);

  const load = useCallback(async () => {
    try {
      setRequests(await gamesApi.getJoinRequests(gameId));
    } catch (err) {
      setError(err.name === 'ApiError' ? err.body?.error || 'Could not load join requests' : 'Could not load join requests');
    }
  }, [gameId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) return null;

  const act = async (work, fallback) => {
    setError('');
    setBusy(true);
    try {
      await work();
      await load();
      onChanged();
    } catch (err) {
      setError(err.name === 'ApiError' ? err.body?.error || fallback : fallback);
    } finally {
      setBusy(false);
    }
  };

  // Block bodies, so a button is handed a handler that returns nothing rather than act's
  // promise: act settles its own errors, and React ignores what a handler returns anyway.
  const approve = (request) => {
    act(() => gamesApi.approveJoinRequest(gameId, request.id), 'Could not let them in');
  };
  const decline = (request) => {
    act(() => gamesApi.declineJoinRequest(gameId, request.id), 'Could not decline the request');
  };
  const remove = (player) => {
    if (!window.confirm(`Take ${player.displayName} out of this game?`)) return;
    act(() => gamesApi.removePlayer(gameId, player.id), 'Could not remove that player');
  };

  const others = (game.players || []).filter((player) => player.id !== currentUser?.id);
  const until = game.rolloverWindowClosesAt
    ? `until ${formatKickoff(game.rolloverWindowClosesAt)}`
    : 'until the next round starts';

  return (
    <section className="gd-panel gd-rollover-panel" aria-label="After the rollover">
      <div className="gd-panel-main">
        <p className="gd-panel-title">
          <RotateCcw size={16} aria-hidden="true" /> Your game rolled over in week {game.lastRolloverWeek}
        </p>
        <p className="gd-panel-body">
          Everybody is back in with a fresh start. You decide who carries on {until}: let new
          people in, or take out anyone who is not playing again.
        </p>
        <p className="gd-panel-warning">
          Before you let anyone in: some people pay their stake again after a rollover, and
          newcomers can make the prize bigger. Whether that is fair is your call.
        </p>

        <h3 className="gd-rollover-heading">Asking to join</h3>
        {requests.length === 0 ? (
          <p className="gd-panel-body">Nobody yet. Anyone with the game code can ask.</p>
        ) : (
          <ul className="gd-rollover-list">
            {requests.map((request) => (
              <li key={request.id} className="gd-rollover-item">
                <span className="gd-rollover-name">{request.displayName}</span>
                <button type="button" className="gd-panel-btn is-add" disabled={busy}
                        onClick={() => approve(request)}>
                  Let {request.displayName} in
                </button>
                <button type="button" className="gd-panel-btn is-remove" disabled={busy}
                        onClick={() => decline(request)}>
                  Decline
                </button>
              </li>
            ))}
          </ul>
        )}

        <h3 className="gd-rollover-heading">In the game</h3>
        <ul className="gd-rollover-list">
          {others.map((player) => (
            <li key={player.id} className="gd-rollover-item">
              <span className="gd-rollover-name">{player.displayName}</span>
              <button type="button" className="gd-panel-btn is-remove" disabled={busy}
                      onClick={() => remove(player)}>
                Remove {player.displayName}
              </button>
            </li>
          ))}
        </ul>

        {game.gameCode ? (
          <p className="gd-panel-body">Game code: <strong>{game.gameCode}</strong></p>
        ) : null}
        {error ? <p className="gd-panel-error" role="alert">{error}</p> : null}
      </div>
    </section>
  );
}

export default RolloverPanel;
